import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// NOTE: こだわり検索パネルはサブパネルを重ねて開くため、最前面のダイアログだけがEsc・トラップに反応するようスタックで管理する
const dialogStack: HTMLElement[] = [];
let bodyOverflowBeforeLock = "";

function focusableElementsIn(dialog: HTMLElement): HTMLElement[] {
  return Array.from(
    dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter((el) => el.getClientRects().length > 0);
}

function focusFirstElement(dialog: HTMLElement) {
  const [first] = focusableElementsIn(dialog);
  if (first) first.focus();
}

type Options = {
  onClose: () => void;
};

function usePortalDialog({ onClose }: Options) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const trigger = document.activeElement as HTMLElement | null;
    dialogStack.push(dialog);
    if (dialogStack.length === 1) {
      bodyOverflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    focusFirstElement(dialog);

    const isTopMost = () => dialogStack[dialogStack.length - 1] === dialog;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopMost()) return;

      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = focusableElementsIn(dialog);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const isOutside = !dialog.contains(active);
      if (e.shiftKey && (active === first || isOutside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || isOutside)) {
        e.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as Node;
      if (!isTopMost() || dialog.contains(target)) return;
      // NOTE: ダイアログ内の非フォーカス要素へのmousedownでは、ブラウザが祖先（main等）へフォーカスを移す。
      //       これを逸出と誤検知して奪還するとクリック途中にスクロールが飛びクリック自体が不発になるため、
      //       祖先へのフォーカスは無視する（祖先からのTab移動は既存のkeydownハンドラが囲い込む）
      if (target instanceof Node && target.contains(dialog)) return;
      focusFirstElement(dialog);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      const index = dialogStack.indexOf(dialog);
      if (index !== -1) dialogStack.splice(index, 1);
      if (dialogStack.length === 0) {
        document.body.style.overflow = bodyOverflowBeforeLock;
      }
      // NOTE: 検索実行後の再描画でトリガが消えている場合があるため、DOMに残っている時だけ戻す
      if (trigger && trigger.isConnected) trigger.focus();
    };
  }, []);

  return dialogRef;
}

export default usePortalDialog;
